import { supabase } from '@/lib/supabase';
import type { DisciplineWithCourses } from '@/lib/types';
import { isObjectEmpty } from '@/utils';

import { getCourseMap, updateCourseMap } from '../queries/useCourseMap';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

// Helper function to recursively update discipline names in course map
const eachRecursive = (obj: any, previousName = '', newName = '') => {
	// eslint-disable-next-line
	for (const k in obj) {
		if (Object.hasOwn(obj, k)) {
			if (typeof obj[k] === 'object' && obj[k] !== null) {
				eachRecursive(obj[k], previousName, newName);
			}

			if (k === 'discipline' && obj[k] === previousName) {
				obj[k] = newName;
			}
			if (k === 'name' && obj[k] === previousName) {
				obj[k] = newName;
			}
			if (k === 'isDiscipline' && obj[k] === previousName) {
				obj[k] = newName;
				obj.id = obj.id.replace(previousName, newName);
			}
		}
	}
	return obj;
};

// Update course map when deleting a discipline (removes discipline from map)
const updateCourseMapOnDelete = async (previousName: string) => {
	const mapJSON = await getCourseMap();

	if (isObjectEmpty(mapJSON)) {
		return { affected_rows: 0 };
	}

	const filteredChildren = (mapJSON.children as any[]).filter(
		(child: any) => child.name !== previousName
	);
	const newChildren = (mapJSON.children as any[]).find(
		(child: any) => child.name === previousName
	)?.children;
	const updateIdx = filteredChildren.findIndex((child: any) => child.name === '');
	// move existing children to new parent
	if (newChildren && newChildren?.length !== 0 && filteredChildren[updateIdx]?.children) {
		filteredChildren[updateIdx].children = [
			...newChildren,
			...(filteredChildren[updateIdx].children ?? []),
		];
	}

	// update base node children array
	mapJSON.children = filteredChildren;

	return updateCourseMap({ map: eachRecursive(mapJSON, previousName, '') });
};

// Update course map when discipline name changes
const updateCourseMapDisciplineName = async (previousName: string, newName: string) => {
	const mapJSON = await getCourseMap();

	if (isObjectEmpty(mapJSON)) {
		return { affected_rows: 0 };
	}

	return updateCourseMap({ map: eachRecursive(mapJSON, previousName, newName) });
};

interface ColorRefObj {
	newColor: string;
	newName: string;
	previousName: string;
}

// Update course map when discipline color changes
const updateCourseMapColors = async (refObj: ColorRefObj) => {
	const { newColor, newName, previousName } = refObj;
	const mapJSON = await getCourseMap();

	if (isObjectEmpty(mapJSON)) {
		return { affected_rows: 0 };
	}
	// find course within the course map that has the previous of name or new name
	const childRef = (mapJSON.children as any[]).find(
		(child: any) => child.name === (newName || previousName)
	);
	if (childRef) {
		childRef.color = newColor;
	}

	return updateCourseMap({ map: mapJSON });
};

// Update course map when migrating courses from one discipline to another
const updateCourseMapOnMigration = async (previousName: string, newName: string) => {
	const mapJSON = await getCourseMap();

	if (isObjectEmpty(mapJSON)) {
		return { affected_rows: 0 };
	}

	const filteredChildren = (mapJSON.children as any[]).filter(
		(child: any) => child.name !== previousName
	);
	const newChildren = (mapJSON.children as any[]).find(
		(child: any) => child.name === previousName
	)?.children;
	const updateIdx = filteredChildren.findIndex((child: any) => child.name === newName);
	// move existing children to new parent
	if (newChildren && newChildren?.length !== 0 && filteredChildren[updateIdx]?.children) {
		filteredChildren[updateIdx].children = [
			...newChildren,
			...(filteredChildren[updateIdx].children ?? []),
		];
	}

	// update base node children array
	mapJSON.children = filteredChildren;

	return updateCourseMap({ map: eachRecursive(mapJSON, previousName, newName) });
};

export interface DisciplinesMutation {
	id: string;
	philosophy: string;
	name: string;
	color: string;
	image: string;
	previousState: any;
}

export const deleteDiscipline = async (variables: { id: string }) => {
	const { id } = variables;

	// First get the discipline to get its name for course map update
	const { data: disciplineData, error: fetchError } = await supabase
		.from('disciplines')
		.select('name')
		.eq('id', id)
		.eq('school_id', schoolId)
		.maybeSingle();

	if (fetchError) throw fetchError;

	// Delete the discipline
	const { data, error } = await supabase
		.from('disciplines')
		.delete()
		.eq('id', id)
		.eq('school_id', schoolId)
		.select('id, name')
		.single();

	if (error) throw error;

	// Update course map if discipline had a name
	if (disciplineData?.name) {
		await updateCourseMapOnDelete(disciplineData.name);
	}

	return data;
};

export const updateCourseDiscipline = async (variables: {
	previousDiscipline: any;
	newDiscipline: any;
}) => {
	const { previousDiscipline, newDiscipline } = variables;

	// Update all courses_disciplines records to point to the new discipline
	const { data, error } = await supabase
		.from('courses_disciplines')
		.update({ discipline_id: newDiscipline.id })
		.eq('discipline_id', previousDiscipline.id)
		.select('course_id, courses_disciplines_id, discipline_id');

	if (error) throw error;

	// Update course map
	if (previousDiscipline.name && newDiscipline.name) {
		await updateCourseMapOnMigration(previousDiscipline.name, newDiscipline.name);
	}

	// Delete the old discipline
	await deleteDiscipline({
		id: previousDiscipline.id,
	});

	return data;
};

export const updateDisciplines = async (variables: DisciplinesMutation) => {
	const { id, philosophy, name, color, image, previousState } = variables;

	// Update course map if name changed
	if (previousState?.name !== name && previousState) {
		await updateCourseMapDisciplineName(previousState.name ?? '', name);
	}

	// Update course map if color changed
	if (previousState?.color !== color && previousState) {
		await updateCourseMapColors({
			previousName: previousState.name ?? '',
			newName: name,
			newColor: color,
		});
	}

	// Update the discipline
	const { data, error } = await supabase
		.from('disciplines')
		.update({
			philosophy,
			name,
			color,
			image,
		})
		.eq('id', id)
		.eq('school_id', schoolId)
		.select(
			`
			id,
			philosophy,
			name,
			color,
			image,
			courses_disciplines (
				courses_disciplines_id,
				course_id,
				discipline_id
			)
		`
		)
		.single();

	if (error) throw error;

	// Ensure return type matches expected format
	return {
		id: data.id,
		name: data.name,
		philosophy: data.philosophy ?? null,
		color: data.color ?? '',
		image: data.image ?? null,
		courses_disciplines: data.courses_disciplines ?? [],
	} as any;
};

export const createDiscipline = async (variables: {
	name: string;
	philosophy?: string | null;
	color: string;
	image: string;
}) => {
	const { name, philosophy, color, image } = variables;

	const { data, error } = await supabase
		.from('disciplines')
		.insert({
			name,
			philosophy: philosophy ?? null,
			color,
			image,
			school_id: schoolId,
		})
		.select('name, philosophy, id, color, image')
		.single();

	if (error) throw error;

	// Return in the format expected by the component
	return {
		id: data.id,
		name: data.name,
		philosophy: data.philosophy ?? null,
		color: data.color ?? null,
		image: data.image ?? null,
		courses_disciplines: [],
	} as unknown as DisciplineWithCourses;
};
